import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Label, Input, Card } from "@tellescope/design-system";
import { Button, Alert, AlertDescription, LanguageSelector } from "@tellescope/ui";
import { Eye, EyeOff, ArrowLeft, Check, X } from "lucide-react";
import {
  useEnduserRegister,
  useEnduserAuthenticatePassword,
  useEnduserIsLoading,
  useEnduserIsAuthenticated,
  useEnduserLoginFlowError,
  useEnduserClearLoginFlow,
} from "../store/hooks";
import {
  useIsRegistrationDisabled,
  useIsLandingPageDisabled,
  usePortalSchemaHasAttemptedFetch,
  usePortalSchemaStore,
} from "../../shared/store/portalSchemaStore";
import { useOrganization } from "../../shared/store/organizationStore";
import { LoadingScreen } from "../../shared/components/LoadingScreen";
import { i18n } from "@tellescope/i18n";

interface PasswordRequirement {
  key: string;
  label: string;
  validate: (password: string) => boolean;
}

const getPasswordRequirements = (): PasswordRequirement[] => [
  {
    key: "length",
    label: i18n.t("Password must be at least 8 characters", { ns: "auth" }),
    validate: (p) => p.length >= 8,
  },
  {
    key: "uppercase",
    label: i18n.t("Password must contain an uppercase letter", { ns: "auth" }),
    validate: (p) => /[A-Z]/.test(p),
  },
  {
    key: "lowercase",
    label: i18n.t("Password must contain a lowercase letter", { ns: "auth" }),
    validate: (p) => /[a-z]/.test(p),
  },
  {
    key: "numberOrSpecial",
    label: i18n.t("Password must contain a number or special character", { ns: "auth" }),
    validate: (p) => /[0-9@#!$%^&*()]/.test(p),
  },
];

export const EnduserRegisterPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();

  // Store hooks
  const register = useEnduserRegister();
  const authenticatePassword = useEnduserAuthenticatePassword();
  const isLoading = useEnduserIsLoading();
  const isAuthenticated = useEnduserIsAuthenticated();
  const loginFlowError = useEnduserLoginFlowError();
  const clearLoginFlow = useEnduserClearLoginFlow();
  const isRegistrationDisabled = useIsRegistrationDisabled();
  const isLandingPageDisabled = useIsLandingPageDisabled();
  const organization = useOrganization();
  const fetchSchema = usePortalSchemaStore((state) => state.fetchSchema);
  const hasAttemptedSchemaFetch = usePortalSchemaHasAttemptedFetch();

  // Local state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearLoginFlow();
    };
  }, [clearLoginFlow]);

  // This route can be the first one a user visits (deep link, bookmark, new tab), so the
  // schema may not have been fetched yet by EnduserRouteHandler. Fetch it here too so
  // isRegistrationDisabled reflects the real setting instead of silently defaulting to false.
  useEffect(() => {
    if (organization) {
      fetchSchema(organization.portalV2SchemaURL);
    }
  }, [organization, fetchSchema]);

  // Password validation
  const passwordValidation = useMemo(() => {
    return getPasswordRequirements().map((req) => ({
      ...req,
      isValid: req.validate(password),
    }));
  }, [password]);

  const isPasswordValid = useMemo(() => {
    return passwordValidation.every((req) => req.isValid);
  }, [passwordValidation]);

  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const isFormValid = useMemo(() => {
    return email.includes("@") && isPasswordValid && passwordsMatch;
  }, [email, isPasswordValid, passwordsMatch]);

  const handleSubmit = useCallback(async () => {
    if (!isFormValid) return;

    setLocalError(null);

    // Register the user
    const registerSuccess = await register({ email, password });
    if (!registerSuccess) {
      // Error will be set in store
      return;
    }

    // Auto-login after registration
    const loginSuccess = await authenticatePassword(email, password);
    if (loginSuccess) {
      navigate("/", { replace: true });
    } else {
      // Registration succeeded but login failed - redirect to login
      setLocalError(t("Account created. Please sign in."));
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 2000);
    }
  }, [isFormValid, email, password, register, authenticatePassword, navigate, t]);

  const handleBack = useCallback(() => {
    navigate("/");
  }, [navigate]);

  const error = loginFlowError || localError;

  // Wait for the schema fetch to resolve before deciding whether registration is allowed —
  // otherwise isRegistrationDisabled reads its default (false) and briefly/permanently
  // allows registration when this is the first route the user has visited.
  if (organization && !hasAttemptedSchemaFetch) {
    return <LoadingScreen />;
  }

  // Redirect if registration is disabled
  if (isRegistrationDisabled) {
    return <Navigate to="/login" replace />;
  }

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <Card variant="floating" padding="root" className="w-full max-w-md">
        <Card.Header className="text-center">
          <Card.Title className="text-2xl font-bold">{t("Create Account")}</Card.Title>
          <Card.Description>{t("Enter your email and create a password")}</Card.Description>
        </Card.Header>

        <Card.Body className="space-y-4">
          {/* Email field */}
          <div className="space-y-2">
            <Label htmlFor="email">{t("Email")}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              autoComplete="email"
              autoFocus
            />
          </div>

          {/* Password field */}
          <div className="space-y-2">
            <Label htmlFor="password">{t("Password")}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>

          {/* Password requirements */}
          {password.length > 0 && (
            <div className="space-y-1">
              {passwordValidation.map((req) => (
                <div
                  key={req.key}
                  className={`flex items-center gap-2 text-xs ${
                    req.isValid ? "text-green-600" : "text-muted-foreground"
                  }`}
                >
                  {req.isValid ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                  <span>{req.label}</span>
                </div>
              ))}
            </div>
          )}

          {/* Confirm password field */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t("Confirm Password")}</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-xs text-destructive">{t("Passwords do not match")}</p>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </Card.Body>

        <Card.Footer className="flex flex-col gap-3">
          <Button onClick={handleSubmit} disabled={!isFormValid || isLoading} className="w-full">
            {isLoading ? t("Creating Account...") : t("Create Account")}
          </Button>

          {!isLandingPageDisabled && (
            <Button variant="ghost" onClick={handleBack} className="w-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("Back")}
            </Button>
          )}

          <div className="text-center text-sm">
            <span className="text-muted-foreground">{t("Already have an account?")} </span>
            <Link to="/login" className="text-primary hover:underline">
              {t("Sign in")}
            </Link>
          </div>

          <div className="w-full flex justify-center pt-2">
            <LanguageSelector />
          </div>
        </Card.Footer>
      </Card>
    </div>
  );
};
